<script setup>
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import otpChannel from 'dashboard/api/channel/otpChannel';
import { useOtpService } from './composables/useOtpService';
import { useWhatsappQrSession } from 'dashboard/composables/useWhatsappQrSession';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ApiCredentials from './components/ApiCredentials.vue';
import DeliveryLogTable from './components/DeliveryLogTable.vue';

const { t } = useI18n();
const alert = useAlert;
const route = useRoute();
const router = useRouter();

const { service, loading, deliveries, load, create, remove, connectChannel } =
  useOtpService();

const serviceName = ref('');
const busy = ref(false);
const showConnection = ref(false);

const {
  sessionKey,
  qrImage,
  status: qrStatus,
  errorMessage: qrError,
  isIdle: qrIdle,
  isConnected: qrConnected,
  hasError: qrHasError,
  generateQr,
  resetSession,
} = useWhatsappQrSession({
  createSession: signal => otpChannel.createWhatsappSession({ signal }),
  getSession: (key, signal) => otpChannel.getWhatsappSession(key, { signal }),
  onConnected: async () => {
    await connectChannel(sessionKey.value);
    showConnection.value = false;
  },
});

async function handleCreate() {
  busy.value = true;
  try {
    await create(serviceName.value);
    resetSession();
  } catch (error) {
    alert(error?.response?.data?.error || t('OTP_MESSAGES.ERROR_LOADING'));
  } finally {
    busy.value = false;
  }
}

async function handleDelete() {
  const confirmed = t('OTP_MESSAGES.DELETE_CONFIRM');
  if (confirmed && !window.confirm(confirmed)) return;
  busy.value = true;
  try {
    await remove();
    resetSession();
    showConnection.value = false;
  } catch (error) {
    alert(error?.response?.data?.error || t('OTP_MESSAGES.ERROR_LOADING'));
  } finally {
    busy.value = false;
  }
}

function openLog() {
  router.push({
    name: 'otp_messages_settings_log',
    params: { accountId: route.params.accountId },
  });
}

onMounted(load);

function handleReconnect() {
  showConnection.value = true;
  generateQr();
}
</script>

<template>
  <div class="flex flex-col gap-6 w-full max-w-3xl py-6">
    <div class="flex flex-col gap-1">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ $t('OTP_MESSAGES.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('OTP_MESSAGES.DESCRIPTION') }}
      </p>
    </div>

    <Spinner v-if="loading" class="size-8" />

    <template v-else-if="!service">
      <div class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('OTP_MESSAGES.SERVICE_NAME') }}
        </label>
        <input
          v-model="serviceName"
          type="text"
          class="px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-sm text-n-slate-12"
        />
        <div>
          <NextButton
            solid
            blue
            :disabled="busy"
            :label="$t('OTP_MESSAGES.CREATE_SERVICE')"
            icon="i-lucide-key-round"
            @click="handleCreate"
          />
        </div>
      </div>
    </template>

    <template v-else>
      <div
        v-if="service.channel_connected"
        class="flex items-center gap-2 p-3 rounded-lg bg-n-teal-3 text-n-teal-11"
      >
        <span class="i-lucide-circle-check size-5" />
        {{
          $t('OTP_MESSAGES.CHANNEL_CONNECTED', { phone: service.channel_phone })
        }}
      </div>

      <NextButton
        v-if="service.channel_connected && !showConnection"
        sm
        :label="$t('OTP_MESSAGES.RECONNECT_CHANNEL')"
        icon="i-lucide-qr-code"
        @click="handleReconnect"
      />

      <div
        v-if="!service.channel_connected || showConnection"
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('OTP_MESSAGES.CONNECT_CHANNEL') }}
        </h3>

        <div v-if="qrIdle">
          <NextButton
            solid
            blue
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.QR_SCREEN.GENERATE')"
            icon="i-lucide-qr-code"
            @click="generateQr"
          />
        </div>

        <div v-else-if="qrHasError" class="flex flex-col gap-3">
          <div
            class="flex items-center gap-2 p-3 rounded-lg bg-n-ruby-3 text-n-ruby-11"
          >
            <span class="i-lucide-circle-alert size-5" />
            {{ qrError }}
          </div>
          <NextButton
            sm
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.QR_SCREEN.RETRY')"
            @click="generateQr"
          />
        </div>

        <div v-else-if="!qrConnected" class="flex flex-col items-center gap-3">
          <div
            class="flex items-center justify-center bg-white border border-n-weak rounded-xl p-3"
          >
            <img
              v-if="qrImage && qrStatus === 'qr'"
              :src="qrImage"
              :alt="$t('OTP_MESSAGES.CONNECT_CHANNEL')"
              class="w-56 h-56"
            />
            <Spinner v-else class="size-10" />
          </div>
        </div>
      </div>

      <ApiCredentials :service="service" />

      <DeliveryLogTable :deliveries="deliveries" />

      <div class="flex items-center gap-3">
        <NextButton
          sm
          faded
          slate
          :label="$t('OTP_MESSAGES.VIEW_LOG')"
          icon="i-lucide-scroll-text"
          @click="openLog"
        />

        <NextButton
          sm
          faded
          slate
          :disabled="busy"
          :label="$t('OTP_MESSAGES.DELETE_SERVICE')"
          icon="i-lucide-trash-2"
          @click="handleDelete"
        />
      </div>
    </template>
  </div>
</template>
